// src/utils/analytics.ts - Privacy-compliant analytics with GA4
interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  functional: boolean;
}

// Replace with your actual GA4 Measurement ID
const GA_MEASUREMENT_ID = "G-XXXXXXXXXX";

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

export const getCookiePreferences = (): CookiePreferences | null => {
  if (typeof window === "undefined") return null;
  const saved = localStorage.getItem("cookie-preferences");
  return saved ? JSON.parse(saved) : null;
};

export const trackEvent = (
  eventName: string,
  parameters?: Record<string, any>
) => {
  const preferences = getCookiePreferences();

  // Only track if user has consented to analytics cookies
  if (preferences?.analytics && typeof window !== "undefined" && window.gtag) {
    window.gtag("event", eventName, parameters);
  }
};

export const trackPageView = (path: string, title?: string) => {
  const preferences = getCookiePreferences();

  if (preferences?.analytics && typeof window !== "undefined" && window.gtag) {
    window.gtag("config", GA_MEASUREMENT_ID, {
      page_title: title || document.title,
      page_location: window.location.href,
      page_path: path,
    });
  }
};

// Conversion tracking functions
export const trackCultureLabComplete = (
  businessType: string,
  challengesCount: number,
  propertyType: string
) => {
  trackEvent("culture_lab_complete", {
    business_type: businessType,
    challenges_count: challengesCount,
    property_type: propertyType,
    value: 1, // Assign value for conversion tracking
  });
};

export const trackConsultationRequest = (
  source: string,
  businessType?: string
) => {
  trackEvent("consultation_request", {
    source: source,
    business_type: businessType,
    value: 5, // Higher value for qualified leads
  });
};

export const trackMagDropsQuote = (
  packageTitle: string,
  businessType: string,
  pages: string
) => {
  trackEvent("magdrops_quote_complete", {
    package_selected: packageTitle,
    business_type: businessType,
    pages: pages,
    value: packageTitle.toLowerCase().includes("editor") ? 6000 : 2000,
  });
};

export const trackNewsletterSignup = (source: string) => {
  trackEvent("newsletter_signup", {
    source: source,
    value: 2, // Value for newsletter subscribers
  });
};

// Enhanced page view tracking with automatic SEO integration
export const trackPageViewWithSEO = (path: string, seoTitle: string) => {
  trackPageView(path, seoTitle);

  // Track page engagement
  trackEvent("page_view", {
    page_path: path,
    page_title: seoTitle,
  });
};

// Usage examples and conversion goals:
// trackCultureLabComplete('property', 3, 'retail_space');
// trackConsultationRequest('culture_lab_results', 'property');
// trackMagDropsQuote('Editor Run', 'hospitality', '12');
// trackNewsletterSignup('culture_lab_completion');
